import { useEffect, useState, useSyncExternalStore } from 'react';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { CHAIN_IDS } from '@/constants/chain';

import { ChainAddress } from '@/utils/models';

type SlippagePreset = 0.001 | 0.0025 | 0.005 | 0.01 | 0.05; // Percentages

// Rebalanced from usage data: dropped 0.75% (weakest preset), added
// 5% (top high-end demand — no preset previously served the >1% population).
export const SLIPPAGE_PRESETS: SlippagePreset[] = [0.001, 0.0025, 0.005, 0.01, 0.05];
export const DEFAULT_SLIPPAGE_PERCENT = 0.005;
export const SLIPPAGE_BPS_MULTIPLIER = 10_000;
const CURRENT_SETTINGS_VERSION = 4;
const MARKET_STORAGE_KEY = 'matcha-market-trade-settings-storage';
const CROSS_CHAIN_STORAGE_KEY = 'matcha-cross-chain-settings-storage';

/** Slippage mode: `auto` resolves per-trade, `manual` uses stored `slippage`. */
export type SlippageMode = 'auto' | 'manual';

type MarketTradeSettings = {
  slippage: number;
  isSlippageCustom: boolean;
  slippageMode: SlippageMode;
};

interface MarketTradeSettingsStore {
  /**
   * Whether Matcha Auto is on. `undefined` means the user never chose, and the
   * default resolves per chain via `resolveIsMatchaAutoOn` (on for Ethereum
   * mainnet, off elsewhere). An explicit toggle persists `true`/`false` and
   * wins on every chain. Do not rename this field back to `isGasless`: the
   * rename is what reset pre-rename storage, and renaming back would not
   * restore anyone's prior state.
   */
  isMatchaAutoOn: boolean | undefined;
  settings: MarketTradeSettings;
  /** The recipient address for swap-and-send (undefined means send to self) */
  recipientAddress: ChainAddress | undefined;
  /** The separately entered receiving address for cross-chain swaps */
  pastedReceivingWalletAddress: ChainAddress | undefined;
  /**
   * Whether the user has altered the settings. Matcha Auto is deliberately
   * excluded: it is a lane choice, not a setting, so it never trips the
   * altered-settings indicator and `resetSettings` leaves it alone.
   */
  hasAlteredSettings: () => boolean;
  resetSettings: () => void;
  setSettings: (settings: Partial<MarketTradeSettings>) => void;
  setIsMatchaAutoOn: (isMatchaAutoOn: boolean) => void;
  setRecipientAddress: (address: ChainAddress | undefined) => void;
  setPastedReceivingWalletAddress: (address: ChainAddress | undefined) => void;
}

const INITIAL_SETTINGS: MarketTradeSettings = {
  slippage: DEFAULT_SLIPPAGE_PERCENT,
  isSlippageCustom: false,
  slippageMode: 'auto',
};

const hasAlteredSlippageSettings = (settings: MarketTradeSettings) =>
  settings.slippage !== INITIAL_SETTINGS.slippage ||
  settings.isSlippageCustom !== INITIAL_SETTINGS.isSlippageCustom ||
  settings.slippageMode !== INITIAL_SETTINGS.slippageMode;

type OldMarketTradeSettings = Partial<MarketTradeSettings> & {
  isGasless?: boolean;
  dynamicSlippage?: boolean;
};

/**
 * Persist migration for the market trade settings store. Exported for unit testing.
 *
 * - pre-v2 state resets to fresh defaults.
 * - v2 -> v3: collapse per-chain market slippage into one global setting.
 * - v3 -> v4: strip `isMatchaAutoOn` so everyone resets once to the per-chain
 *   default (on for Ethereum mainnet, off elsewhere). A persisted `false` from
 *   the off-for-everyone era is indistinguishable from a deliberate opt-out,
 *   so the hard reset is deliberate.
 *
 * No version ever migrates the old `isGasless` field into `isMatchaAutoOn`:
 * the reset is deliberate.
 */
export function migrateMarketTradeSettings(
  persistedState: unknown,
  version: number,
): MarketTradeSettingsStore {
  if (version < 4) {
    delete (persistedState as { isMatchaAutoOn?: boolean }).isMatchaAutoOn;
  }

  const rePersistedSettings = (
    persistedState as { settings?: Record<number, OldMarketTradeSettings> | MarketTradeSettings }
  ).settings;
  if (
    version < 3 &&
    typeof rePersistedSettings === 'object' &&
    rePersistedSettings !== null &&
    'slippageMode' in rePersistedSettings
  ) {
    return persistedState as MarketTradeSettingsStore;
  }

  if (version < 2) {
    return {
      settings: { ...INITIAL_SETTINGS },
    } as MarketTradeSettingsStore;
  }

  if (version < 3) {
    const typed = persistedState as {
      settings?: Record<number, OldMarketTradeSettings> | MarketTradeSettings;
    };

    const settingsByChain =
      typeof typed.settings === 'object' && typed.settings !== null
        ? (typed.settings as Record<number, OldMarketTradeSettings>)
        : {};
    const manualSettings = Object.values(settingsByChain).filter(
      (setting) => setting.slippageMode === 'manual',
    );
    const solanaSettings = settingsByChain[CHAIN_IDS.SOLANA];
    if (solanaSettings?.dynamicSlippage === false && !manualSettings.includes(solanaSettings)) {
      manualSettings.push(solanaSettings);
    }

    const manualValues = new Set(manualSettings.map((setting) => setting.slippage));
    const hasConflictingManualValues = manualValues.size > 1;
    const agreedManualSetting = manualSettings[0];

    typed.settings =
      agreedManualSetting && !hasConflictingManualValues
        ? {
            ...INITIAL_SETTINGS,
            slippage: agreedManualSetting.slippage ?? DEFAULT_SLIPPAGE_PERCENT,
            isSlippageCustom: manualSettings.some((setting) => setting.isSlippageCustom === true),
            slippageMode: 'manual',
          }
        : { ...INITIAL_SETTINGS };
  }

  return persistedState as MarketTradeSettingsStore;
}

const useInternalMarketTradeSettingsStore = createWithEqualityFn<MarketTradeSettingsStore>()(
  persist(
    (set, get) => ({
      isMatchaAutoOn: undefined,
      settings: { ...INITIAL_SETTINGS },
      recipientAddress: undefined,
      pastedReceivingWalletAddress: undefined,
      hasAlteredSettings: () => {
        return hasAlteredSlippageSettings(get().settings);
      },
      resetSettings: () =>
        set((state) => ({
          settings: { ...INITIAL_SETTINGS },
          isMatchaAutoOn: state.isMatchaAutoOn,
        })),
      setSettings: (settings: Partial<MarketTradeSettings>) =>
        set((state) => ({
          settings: { ...state.settings, ...settings },
          isMatchaAutoOn: state.isMatchaAutoOn,
        })),
      setIsMatchaAutoOn: (isMatchaAutoOn: boolean) =>
        set((state) => ({
          isMatchaAutoOn,
          settings: state.settings,
        })),
      setRecipientAddress: (address: ChainAddress | undefined) =>
        set(() => ({
          recipientAddress: address,
        })),
      setPastedReceivingWalletAddress: (address: ChainAddress | undefined) =>
        set(() => ({
          pastedReceivingWalletAddress: address,
        })),
    }),
    {
      name: MARKET_STORAGE_KEY,
      skipHydration: true,
      version: CURRENT_SETTINGS_VERSION,
      partialize: (state) => ({
        isMatchaAutoOn: state.isMatchaAutoOn,
        settings: state.settings,
      }),
      migrate: migrateMarketTradeSettings,
    },
  ),
  shallow,
);

/**
 * Ensures the persisted store hydrates after the initial render to prevent hydration mismatches.
 */
export function useMarketTradeSettingsStore<T>(
  selector: (state: MarketTradeSettingsStore) => T = (x) => x as T,
) {
  const [rehydrated, setIsRehydrated] = useState(false);

  useEffect(() => {
    if (!rehydrated) {
      localStorage.removeItem(CROSS_CHAIN_STORAGE_KEY);
      useInternalMarketTradeSettingsStore.persist.rehydrate();
      setIsRehydrated(true);
    }
  }, [rehydrated]);

  return useInternalMarketTradeSettingsStore(selector);
}

/**
 * Whether the persisted market settings have hydrated. With `skipHydration`
 * the first client render sees the in-memory defaults, so consumers resolving
 * the Matcha Auto default must wait for this to avoid acting on a state the
 * user may have explicitly overridden.
 */
export function useMarketSettingsHydrated() {
  return useSyncExternalStore(
    (callback) => useInternalMarketTradeSettingsStore.persist.onFinishHydration(callback),
    () => useInternalMarketTradeSettingsStore.persist.hasHydrated(),
    () => false,
  );
}

export const resetSettingsSelector = (state: MarketTradeSettingsStore) => state.resetSettings;

/** Resolves the tri-state toggle: an explicit user choice wins everywhere; untouched defaults to on for Ethereum mainnet only. */
export const resolveIsMatchaAutoOn = (
  isMatchaAutoOn: boolean | undefined,
  chainId: number,
): boolean => isMatchaAutoOn ?? chainId === CHAIN_IDS.MAINNET;

export const matchaAutoSelectors = (state: MarketTradeSettingsStore) => ({
  isMatchaAutoOn: state.isMatchaAutoOn,
  setIsMatchaAutoOn: state.setIsMatchaAutoOn,
});

export const slippageSelector = (state: MarketTradeSettingsStore) => ({
  ...state.settings,
  setSettings: state.setSettings,
});

export const hasAlteredSettingsSelector = (state: MarketTradeSettingsStore) =>
  state.hasAlteredSettings();

export const hasAlteredSlippageSettingsSelector = (state: MarketTradeSettingsStore) =>
  hasAlteredSlippageSettings(state.settings);

export const recipientAddressSelector = (state: MarketTradeSettingsStore) => ({
  recipientAddress: state.recipientAddress,
  setRecipientAddress: state.setRecipientAddress,
});

export const pastedReceivingWalletAddressSelector = (state: MarketTradeSettingsStore) => ({
  pastedReceivingWalletAddress: state.pastedReceivingWalletAddress,
  setPastedReceivingWalletAddress: state.setPastedReceivingWalletAddress,
});
